// Pure routing decision for the `/` HomePage. Returns the URL to redirect to, or `null` to keep showing the spinner.

// Relative rather than the `core/*` tsconfig alias: vitest.config.ts only
// mirrors the `dashboard/*` and `organizations/*` aliases, so an aliased
// `core/*` value import would fail to resolve under test.
import {
  buildClassicWorkspaceEntryPath,
  buildClassicWorkspacesNewPath,
} from "../../core/lib/classic-path"
import { hasOrganizationId } from "../../core/lib/organization-id"
import { NEW_ORGANIZATION_PATH } from "../../core/lib/legacy-classic-redirect"
import { buildDashboardPath } from "dashboard/lib/dashboard-path"

export interface HomeRedirectInput {
  isAuthenticated: boolean
  user: { user_id?: string; needs_onboarding?: boolean } | undefined
  isUserPending: boolean
  workspaces:
    | Array<{
        workspace_id: string
        is_sandbox?: boolean
        organization_id?: string | null
      }>
    | undefined
  isLoadingWorkspaces: boolean
  workspacesError: boolean
  organizations: Array<{ organization_id: string }>
  isLoadingOrgs: boolean
  orgsError: boolean
  preferredWorkspaceId: string | null
  deniedWorkspaceIds: string[]
  /** undefined while the flag value is still loading (cache miss + /flags in flight). */
  auroraEnabled: boolean | undefined
  /** Per-browser preference set when the user clicks "Switch to classic/dashboard"; `null` means honor the Aurora flag default. */
  preferredConsoleMode: "classic" | "dashboard" | null
}

export function decideHomeRedirect(input: HomeRedirectInput): string | null {
  const {
    isAuthenticated,
    user,
    isUserPending,
    workspaces,
    organizations,
    isLoadingOrgs,
    orgsError,
    preferredWorkspaceId,
    deniedWorkspaceIds,
    auroraEnabled,
    preferredConsoleMode,
  } = input

  if (!isAuthenticated) {
    return "/signin"
  }

  // Wait for the session before choosing a landing. /me carries the server
  // preferred workspace and needs_onboarding, so redirecting while it is still
  // pending would ignore the server preference and land on a stale local guess.
  if (isUserPending) {
    return null
  }

  const accessibleWorkspaces =
    workspaces == null
      ? undefined
      : deniedWorkspaceIds.length === 0
      ? workspaces
      : workspaces.filter((w) => !deniedWorkspaceIds.includes(w.workspace_id))

  const isPreferredValid =
    preferredWorkspaceId != null &&
    (accessibleWorkspaces?.some(
      (w) => w.workspace_id === preferredWorkspaceId
    ) ??
      false)

  // New signups no longer go through an onboarding wizard; the flag now only
  // decides where they land on this first visit.
  const needsOnboarding = user?.needs_onboarding === true

  // Self-serve signup provisions both a production and a sandbox workspace,
  // and the list order is not guaranteed — so start a brand-new user in
  // production rather than whichever came back first. The onboarding wizard's
  // finish step used to make the same choice. Workspaces with no is_sandbox
  // field count as production.
  const firstProductionWorkspaceId = accessibleWorkspaces?.find(
    (w) => w.is_sandbox !== true
  )?.workspace_id
  const defaultWorkspaceId = needsOnboarding
    ? firstProductionWorkspaceId ?? accessibleWorkspaces?.[0]?.workspace_id
    : accessibleWorkspaces?.[0]?.workspace_id

  const targetWorkspaceId = isPreferredValid
    ? preferredWorkspaceId
    : defaultWorkspaceId

  const firstOrganizationId = organizations[0]?.organization_id
  // hasOrganizationId, not a length check: a blank/whitespace org id is absent
  // here for the same reason it is on a workspace — never emit
  // `/organizations/` (or `/organizations//workspaces/new`) with an empty
  // segment.
  const hasOrgs = !orgsError && hasOrganizationId(firstOrganizationId)
  // `/organizations/list` often resolves before `/workspaces/list`. Dropping
  // the user on the org overview in that gap would bounce a user who does have
  // a workspace, so the org page is only a destination once the workspace list
  // has resolved (workspaces != null — loading, paused and errored all leave it
  // undefined) and named no workspace to prefer.
  const shouldRouteToOrgPage = hasOrgs && workspaces != null

  if (targetWorkspaceId != null) {
    // Always wait for the Aurora flag rather than guessing. A cold signup has
    // no localStorage cache to warm it, and useAuroraEnabled falls back to
    // false after 3s — guessing here would drop brand-new users into the
    // classic device list instead of the assistant.
    if (auroraEnabled === undefined) return null

    // Emit an org-scoped URL only when the target workspace's org is already
    // known — never guess one. Otherwise fall back to the legacy shape
    // (`/dashboard/{id}`, `/workspaces/{id}/…`): the compat resolvers turn
    // those into the org-scoped URL or the interstitial once the workspace
    // list resolves. Both consoles read the same variable, so they can never
    // disagree about which org a workspace belongs to.
    const rawTargetOrganizationId = accessibleWorkspaces?.find(
      (w) => w.workspace_id === targetWorkspaceId
    )?.organization_id
    // Blank/whitespace-only org ids are treated as absent, same as
    // null/undefined — never build a URL with an empty org segment.
    const targetOrganizationId =
      typeof rawTargetOrganizationId === "string" &&
      rawTargetOrganizationId.trim() !== ""
        ? rawTargetOrganizationId
        : undefined
    const classicDevicesPath = (): string =>
      buildClassicWorkspaceEntryPath({
        organizationId: targetOrganizationId,
        workspaceId: targetWorkspaceId,
        subpath: "/devices",
      })

    // A "dashboard" preference is ignored when the flag is off — dashboard isn't available to that user, so we fall through to classic.
    if (preferredConsoleMode === "classic") {
      return classicDevicesPath()
    }

    const dashboardPath = (subpath?: string): string =>
      targetOrganizationId !== undefined
        ? buildDashboardPath({
            organizationId: targetOrganizationId,
            workspaceId: targetWorkspaceId,
            subpath,
          })
        : `/dashboard/${targetWorkspaceId}${
            subpath == null ? "" : `/${subpath}`
          }`

    if (preferredConsoleMode === "dashboard" && auroraEnabled) {
      return dashboardPath()
    }
    if (!auroraEnabled) {
      return classicDevicesPath()
    }
    // Fresh signups start in the assistant, which is what the onboarding
    // wizard's finish step used to do. Returning users get the Today rundown.
    return needsOnboarding ? dashboardPath("assistant") : dashboardPath()
  }

  if (shouldRouteToOrgPage) {
    return `/organizations/${firstOrganizationId}`
  }

  // Creating a workspace happens inside an organization, so the fallback is
  // that organization's route rather than the pre-ENG-3121 `/workspaces/new`
  // compat URL. A user with no organization at all has to create one first.
  // (An org list we hold but couldn't refresh still names an org to scope to.)
  const newWorkspacePath = hasOrganizationId(firstOrganizationId)
    ? buildClassicWorkspacesNewPath(firstOrganizationId)
    : NEW_ORGANIZATION_PATH

  // Only once both lists have settled: sending the user to workspace creation
  // while either is still in flight (or after a transient workspaces failure)
  // would skip a workspace or org they actually have. A non-null `workspaces`
  // means the list loaded successfully — not loading, paused/offline, or errored
  // (all of which leave it undefined), which `!isLoadingWorkspaces` alone missed.
  if (user != null && workspaces != null && !isLoadingOrgs) {
    return newWorkspacePath
  }

  if (!isUserPending && user == null) {
    // useMe swallows errors; isPending (not isLoading) avoids false positives during React Query's enabled-transition gap.
    return newWorkspacePath
  }

  return null
}
